import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';

import { SERVICES_PROPERTIES } from 'app/config/services';
import { FormServiceContext } from 'app/utils/contexts';

import { setupLinePayload, setupLinesInicialValues } from 'app/utils/forms/services/lines';
import { CircularProgress } from '@material-ui/core';
import { validateLineRequest } from 'app/utils/forms/validators/line';
import {
  clearAllLines,
  clearLineScheduleBoard,
  clearPermissionaries,
  clearSchedulesStore,
} from 'app/store/slices/line';

const Lines = props => {

  const {
    intl,
    classes,
    loading,
    loadingDocuments,
    setLoadingDocuments,
    onCancel,
    onSave,
    onSubmit,
    initialValues,
    setInitialValues,
  } = props;

  const dispatch = useDispatch();

  const internalNewRequest = useSelector(state => state.forms.internalNewRequest);
  const request = useSelector(state => state.requests.details);
  const authUser = useSelector(state => state.auth.user);
  const service = useSelector(state => state.services.details);
  const lines = useSelector(state => state.line.allLines);
  const schedulesStore = useSelector(state => state.line.schedulesStore);
  const selectedLineId = useSelector(state => state.line.selectedLineId);

  const [canEdit] = useState(true);
  const [errorsList, setErrorsList] = useState({});

  useEffect(() => {
    (() => {
      if (Object.keys(internalNewRequest).length > 0) {
        const temp = setupLinesInicialValues(internalNewRequest);
        setInitialValues(temp);
      } else {
        if (SERVICES_PROPERTIES[internalNewRequest.cdServico]) {
          setInitialValues(SERVICES_PROPERTIES[internalNewRequest.cdServico].formInitialValues);
        }
      }
    })();
  }, [internalNewRequest, setInitialValues]);

  useEffect(() => {
    return () => {
      dispatch(clearSchedulesStore());
      dispatch(clearPermissionaries());
      dispatch(clearLineScheduleBoard());
      dispatch(clearAllLines());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = useMemo(() => {
    return SERVICES_PROPERTIES[internalNewRequest.cdServico]
      ? SERVICES_PROPERTIES[internalNewRequest.cdServico].validationSchema
      : {};
  }, [internalNewRequest.cdServico]);

  const setupPayload = useCallback(
    (valuesCopy) => {

      const linhaSelecionada = lines.find(line => line.cdLinha === selectedLineId);

      const payload = setupLinePayload({
        values: valuesCopy,
        cdServico: service.cdServico,
        linha: linhaSelecionada,
        horarios: schedulesStore,
      });
      const errors = validateLineRequest({
        values: payload,
        cdServico: service.cdServico,
        intl,
        isInternalForm: true,
      });
      if (Object.keys(errors).length > 0) {
        setErrorsList(errors);
        return;
      }
      return payload;
    },
    [intl, lines, schedulesStore, service, selectedLineId],
  );

  const handleSave = useCallback(
    (values, fromSubmit, oldData) => {
      onSave(values, fromSubmit, oldData, setupPayload);
    },
    [onSave, setupPayload],
  );

  const handleSubmit = useCallback(
    (values) => {
      onSubmit(values, onSave, setupPayload);
    },
    [onSave, onSubmit, setupPayload],
  );

  const contextProvider = useMemo(() => {
    return {
      isInternalForm: false,
      hideActionBtn: true,
      hideFinalizeBtn: false,
      mainColor: 'secondary',
      loadingDocuments,
      setLoadingDocuments,
      onCancel,
      onSave: handleSave,
      onSubmit: handleSubmit,
      initialValues,
      validationSchema,
      validateOnSave: true,
      canEdit,
      user: authUser,
      errorsList,
      setErrorsList,
    };
  }, [
    authUser,
    canEdit,
    errorsList,
    handleSave,
    handleSubmit,
    initialValues,
    loadingDocuments,
    onCancel,
    setLoadingDocuments,
    validationSchema,
  ]);

  const renderRequest = () => {
    const internalCdServico = internalNewRequest.cdServico || request.cdServico;
    let Component = SERVICES_PROPERTIES[internalCdServico] ? (
      SERVICES_PROPERTIES[internalCdServico].component
    ) : (
      <></>
    );

    return (
      <>
        {loading && (
          <div className={classes.centerProgress}>
            <CircularProgress size={20} />
          </div>
        )}
        {!loading && (
          <FormServiceContext.Provider value={contextProvider}>
            <Component {...props} />
          </FormServiceContext.Provider>
        )}
      </>
    );
  };

  return renderRequest();
};

export default injectIntl(Lines);
